import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";

import Layout from "@/components/Layout";
import ProtectedRoute from "@/components/ProtectedRoute";
import ProtectedAdmin from "@/components/ProtectedAdmin";
import { CookieBanner } from "@/components/CookieBanner";
import Home from "@/pages/Home";

const AppShell = lazy(() => import("@/components/AppShell"));
const AdminLayout = lazy(() => import("@/components/AdminLayout"));

const HowItWorks = lazy(() => import("@/pages/HowItWorks"));
const Features = lazy(() => import("@/pages/Features"));
const PropertyTimeline = lazy(() => import("@/pages/PropertyTimeline"));
const Pricing = lazy(() => import("@/pages/Pricing"));
const Glossary = lazy(() => import("@/pages/Glossary"));
const About = lazy(() => import("@/pages/About"));
const Demo = lazy(() => import("@/pages/Demo"));
const UseCases = lazy(() => import("@/pages/UseCases"));
const Customers = lazy(() => import("@/pages/Customers"));
const Legal = lazy(() => import("@/pages/Legal"));
const Privacy = lazy(() => import("@/pages/Privacy"));
const Terms = lazy(() => import("@/pages/Terms"));
const NotFound = lazy(() => import("@/pages/NotFound"));

const Login = lazy(() => import("@/pages/Login"));
const Register = lazy(() => import("@/pages/Register"));
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const CheckoutReturn = lazy(() => import("@/pages/CheckoutReturn"));
const AcceptInvite = lazy(() => import("@/pages/AcceptInvite"));
const ClientPortal = lazy(() => import("@/pages/ClientPortal"));

const Dashboard = lazy(() => import("@/pages/app/Dashboard"));
const Cases = lazy(() => import("@/pages/app/Cases"));
const CaseDetail = lazy(() => import("@/pages/app/CaseDetail"));
const Documents = lazy(() => import("@/pages/app/Documents"));
const Reminders = lazy(() => import("@/pages/app/Reminders"));
const Settings = lazy(() => import("@/pages/app/Settings"));

const AdminLogin = lazy(() => import("@/pages/admin/AdminLogin"));
const AdminDashboard = lazy(() => import("@/pages/admin/AdminDashboard"));
const AdminUsers = lazy(() => import("@/pages/admin/AdminUsers"));
const AdminContacts = lazy(() => import("@/pages/admin/AdminContacts"));
const AdminContent = lazy(() => import("@/pages/admin/AdminContent"));
const AdminTestimonials = lazy(() => import("@/pages/admin/AdminTestimonials"));

function RouteFallback() {
  return (
    <div className="flex min-h-[60vh] items-center justify-center bg-[#060F1E]" data-testid="route-loading">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-[#00D4AA]/30 border-t-[#00D4AA]" />
    </div>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/billing/return" element={<CheckoutReturn />} />
            <Route path="/team/accept/:token" element={<AcceptInvite />} />

            {/* Admin */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route
              path="/admin"
              element={
                <ProtectedAdmin>
                  <AdminLayout />
                </ProtectedAdmin>
              }
            >
              <Route index element={<AdminDashboard />} />
              <Route path="dashboard" element={<AdminDashboard />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="contacts" element={<AdminContacts />} />
              <Route path="content" element={<AdminContent />} />
              <Route path="testimonials" element={<AdminTestimonials />} />
            </Route>

            <Route
              path="/app"
              element={
                <ProtectedRoute>
                  <AppShell />
                </ProtectedRoute>
              }
            >
              <Route index element={<Dashboard />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="cases" element={<Cases />} />
              <Route path="cases/:id" element={<CaseDetail />} />
              <Route path="documents" element={<Documents />} />
              <Route path="reminders" element={<Reminders />} />
              <Route path="settings" element={<Settings />} />
            </Route>

            {/* Public client portal — no auth, accessed via secure share token */}
            <Route path="/portal/:token" element={<ClientPortal />} />

            <Route element={<Layout />}>
              <Route path="/" element={<Home />} />
              <Route path="/how-it-works" element={<HowItWorks />} />
              <Route path="/features" element={<Features />} />
              <Route path="/use-cases" element={<UseCases />} />
              <Route path="/customers" element={<Customers />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/timeline" element={<PropertyTimeline />} />
              <Route path="/glossary" element={<Glossary />} />
              <Route path="/about" element={<About />} />
              <Route path="/demo" element={<Demo />} />
              <Route path="/waitlist" element={<Demo />} />
              <Route path="/legal" element={<Legal />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="*" element={<NotFound />} />
            </Route>
          </Routes>
          </Suspense>
          <CookieBanner />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
